import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";
import { FaTrash } from "react-icons/fa";
import { useState, useEffect } from "react";

const OrderSummaryTab = ({ checkoutProducts, onUpdateQuantity, onRemoveItem }) => {
  const [quantities, setQuantities] = useState(
    checkoutProducts.reduce((acc, item) => {
      acc[item.productId] = item.quantity;
      return acc;
    }, {})
  );

  useEffect(() => {
    setQuantities(
      checkoutProducts.reduce((acc, item) => {
        acc[item.productId] = item.quantity;
        return acc;
      }, {})
    );
  }, [checkoutProducts]);

  const handleQuantityChange = (productId, action) => {
    setQuantities((prevQuantities) => {
      const newQuantity = action === "increment"
        ? prevQuantities[productId] + 1
        : Math.max(prevQuantities[productId] - 1, 1);

      onUpdateQuantity(productId, newQuantity);

      return {
        ...prevQuantities,
        [productId]: newQuantity,
      };
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mt-1">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
      <div className="space-y-6">
        {checkoutProducts.map((item) => (
          <div
            key={item.productId}
            className="bg-gray-50 rounded-xl p-4 sm:p-6 hover:shadow-md transition-all duration-300"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              {/* Product Image and Details */}
              <div className="flex items-center gap-4 flex-1">
                <div className="relative group">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl ring-1 ring-gray-200 group-hover:scale-105 transition-transform duration-300"
                  />
                  <button
                    onClick={() => onRemoveItem(item.productId)}
                    className="absolute -top-2 -right-2 bg-white rounded-full p-1.5 shadow-md hover:bg-red-50 text-red-500 hover:text-red-600 transition-colors duration-200 opacity-0 group-hover:opacity-100"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                    {item.name}
                  </h3>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Size: {item.variation.size}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Color: {item.variation.color}
                    </span>
                  </div>
                  <p className="mt-2 text-lg font-bold text-blue-600 sm:hidden">
                    ₹{(item.price * quantities[item.productId]).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Quantity Controls and Price */}
              <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-8">
                <div className="flex items-center">
                  <button
                    onClick={() => handleQuantityChange(item.productId, "decrement")}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                    aria-label="Decrease quantity"
                  >
                    <AiOutlineMinus className="text-gray-600" />
                  </button>
                  <span className="w-12 text-center font-semibold text-gray-900">
                    {quantities[item.productId]}
                  </span>
                  <button
                    onClick={() => handleQuantityChange(item.productId, "increment")}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                    aria-label="Increase quantity"
                  >
                    <AiOutlinePlus className="text-gray-600" />
                  </button>
                </div>
                
                <p className="hidden sm:block text-lg font-bold text-blue-600 min-w-[100px] text-right">
                  ₹{(item.price * quantities[item.productId]).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Order Total */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-lg text-gray-600">Total Items:</span>
          <span className="text-lg font-semibold text-gray-900">
            {checkoutProducts.reduce((sum, item) => sum + quantities[item.productId], 0)}
          </span>
        </div>
        <div className="flex justify-between items-center mt-2">
          <span className="text-xl font-semibold text-gray-900">Total Amount:</span>
          <span className="text-2xl font-bold text-blue-600">
            ₹{checkoutProducts
              .reduce((sum, item) => sum + item.price * quantities[item.productId], 0)
              .toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OrderSummaryTab;